import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Descendant } from 'slate'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { AnnotationMetadata } from '@/features/annotation'

import { DEFAULT_MAX_CHARACTERS } from '../../../utils/constants'
import { EditorMode } from '../constants'
import { useAddComment } from '../hooks/hooks'
import type { EditorModeType } from '../types'
import { extractMentionedUserIds } from '../utils/mentionUtils'
import {
  initialEditorValue,
  parseMarkdownToSlate,
  serializeToMarkdown,
  serializeToPlainText,
} from '../utils/slateUtils'

import { RichTextEditor } from './RichTextEditor/RichTextEditor'

const EDITOR_ID = 'add-comment-editor'

export interface AddCommentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  documentVersionId: string
  annotation?: AnnotationMetadata | null
  onSuccess?: (commentId: string) => void
}

export const AddCommentDialog: React.FC<AddCommentDialogProps> = ({
  open,
  onOpenChange,
  documentVersionId,
  annotation,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [editorValue, setEditorValue] =
    useState<Descendant[]>(initialEditorValue)
  const [editorMode, setEditorMode] = useState<EditorModeType>(EditorMode.PLAIN)
  const [isSwitchingMode, setIsSwitchingMode] = useState(false)
  const [isComposing, setIsComposing] = useState(false)
  const { mutate: addComment, isPending: isSaving } = useAddComment()

  // Reset editor value and mode when dialog closes
  useEffect(() => {
    if (!open) {
      setEditorValue(initialEditorValue)
      setEditorMode(EditorMode.PLAIN)
      setIsComposing(false)
    }
  }, [open])

  // Save validation - allow save during composition for IME support (Unikey, etc.)
  const canSave = useMemo(() => {
    const markdown = serializeToMarkdown(editorValue)
    const hasAlphanumeric = /[a-zA-Z0-9]/.test(markdown)

    if (!hasAlphanumeric) return false

    const plain = serializeToPlainText(editorValue).trim()

    // During IME (Unikey) composition → allow saving if user is typing
    if (isComposing) return true

    return plain.length > 0
  }, [editorValue, isComposing])

  const handleSave = useCallback(async () => {
    const markdownContent = serializeToMarkdown(editorValue)
    const mentionedUserIds = extractMentionedUserIds(markdownContent)

    addComment(
      {
        content: markdownContent,
        document_version_id: documentVersionId,
        annotation: annotation || undefined,
        mentioned_user_ids: mentionedUserIds,
      },
      {
        onSuccess: (response) => {
          triggerToast('success', t('tflViewer.addCommentDialog.toast.success'))
          if (onSuccess && response.data?.id) {
            onSuccess(response.data.id)
          }
          setEditorValue(initialEditorValue)
          onOpenChange(false)
        },
        onError: () => {
          triggerToast('error', t('tflViewer.addCommentDialog.toast.error'))
        },
      }
    )
  }, [
    editorValue,
    documentVersionId,
    annotation,
    addComment,
    onSuccess,
    onOpenChange,
    t,
  ])

  const handleCancel = useCallback(() => {
    setEditorValue(initialEditorValue)
    onOpenChange(false)
  }, [onOpenChange])

  const handleModeChange = useCallback(
    (checked: boolean) => {
      const newMode = checked ? EditorMode.MARKDOWN : EditorMode.PLAIN

      if (newMode === EditorMode.MARKDOWN) {
        setEditorMode(EditorMode.MARKDOWN)
      } else {
        setIsSwitchingMode(true)
        try {
          const markdownText = serializeToMarkdown(editorValue)
          const parsedNodes = parseMarkdownToSlate(markdownText)
          setEditorValue(parsedNodes)
          setEditorMode(EditorMode.PLAIN)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Mode switch error:', error)
          setEditorMode(EditorMode.MARKDOWN)
        } finally {
          setIsSwitchingMode(false)
        }
      }
    },
    [editorValue]
  )

  const handleKeyDownCapture = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Allow hotkeys (Cmd/Ctrl + key)
    if (e.metaKey || e.ctrlKey) return

    const target = e.target
    if (
      target instanceof HTMLElement &&
      target.getAttribute('role') === 'textbox'
    ) {
      e.stopPropagation()
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[720px] sm:max-w-[720px] gap-1">
        <DialogHeader>
          <DialogTitle>{`Add ${annotation ? 'section' : 'file'}-level comment`}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('tflViewer.addCommentDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="h-[350px]" onKeyDownCapture={handleKeyDownCapture}>
          <RichTextEditor
            value={editorValue}
            onChange={setEditorValue}
            maxCharacters={DEFAULT_MAX_CHARACTERS}
            autoFocus
            disabled={isSaving || isSwitchingMode}
            id={EDITOR_ID}
            mode={editorMode}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
          />
        </div>

        <div className="flex items-center justify-between w-full gap-3 mt-4">
          <div className="flex items-center flex-1 gap-2 ">
            <Switch
              id="show-markdown"
              checked={editorMode === EditorMode.MARKDOWN}
              onCheckedChange={handleModeChange}
              disabled={isSaving || isSwitchingMode}
            />
            <label
              htmlFor="show-markdown"
              className="text-sm font-normal text-gray-700 cursor-pointer select-none"
            >
              {t('tflViewer.addCommentDialog.showMarkdown')}
            </label>
          </div>
          <div className="flex gap-3">
            <Button
              className="flex-1 rounded-md py-2.5 px-3.5 text-sm font-semibold border-solid border-primary bg-white shadow-sm text-secondary-700"
              variant="default"
              onClick={handleCancel}
              disabled={isSaving}
            >
              {t('tflViewer.addCommentDialog.cancel')}
            </Button>
            <Button
              className="flex-1 rounded-md py-2.5 px-3.5 text-sm whitespace-nowrap font-semibold border-solid border-transparent text-white bg-brand-solid hover:text-white hover:bg-brand-solid"
              variant="default"
              onClick={handleSave}
              disabled={!canSave || isSaving}
            >
              {isSaving ? (
                <div className="flex items-center gap-2 px-1">
                  <Spinner className="size-4" />
                  {t('tflViewer.addCommentDialog.saving')}
                </div>
              ) : (
                t('tflViewer.addCommentDialog.save')
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
